import React, { useState, useContext, useEffect, createContext } from 'react';
import { initialize } from 'launchdarkly-js-client-sdk';
import PropTypes from 'prop-types';
import Cookies from 'universal-cookie';
import { LaunchdarklyEnvironment, NodeEnvironment } from '../../interfaces/env';

export const LDContext = createContext(null);

const hasLocalStorage = typeof localStorage !== 'undefined';

export enum LD_STATUSES {
  FETCHING = 'FETCHING',
  FETCHED = 'FETCHED',
}

//reading from localStorage since viteknology2 cookie may not be available on local or stage
const getUser = () => {
  // eslint-disable-next-line no-undef
  if (SERVER) return;
  let user;
  const cookies = new Cookies();
  if (hasLocalStorage && localStorage.getItem('ld_user') !== null) {
    user = localStorage.getItem('ld_user');
  } else {
    user =
      typeof cookies.get('viteknology2') !== 'undefined'
        ? cookies.get('viteknology2')
        : `lduser-${Math.random()}`;
    if (hasLocalStorage) {
      localStorage.setItem('ld_user', user);
    }
  }
  return user;
};

const LAUNCHDARKLY_PROD_KEY = '5c63ac945943ee2e13539903';
const LAUNCHDARKLY_TEST_KEY = '5c63ac945943ee2e13539902';

const getLaunchdarklyEnv = () => process.env.LAUNCHDARKLY_ENV || window.LAUNCHDARKLY_ENV;

function getLdKey(): string {
  const isProd =
    process.env.NODE_ENV === NodeEnvironment.prod &&
    getLaunchdarklyEnv() === LaunchdarklyEnvironment.prod;
  return isProd ? LAUNCHDARKLY_PROD_KEY : LAUNCHDARKLY_TEST_KEY;
}

export const LDProvider = ({ children }) => {
  const [ldClient, setLdClient] = useState(null);
  const user = getUser();
  useEffect(() => {
    // eslint-disable-next-line no-undef
    if (SERVER) return;
    const ldClient = initialize(getLdKey(), { key: user });
    ldClient.on('ready', () => setLdClient(ldClient));
  }, []);
  return <LDContext.Provider value={ldClient}>{children}</LDContext.Provider>;
};

LDProvider.propTypes = {
  children: PropTypes.any,
};

export const useLD = (key, defaultValue = false, shouldUseLd = true) => {
  const client = useContext(LDContext);
  const [ldValue, setLdValue] = useState([defaultValue, LD_STATUSES.FETCHING]);
  useEffect(() => {
    if (client && shouldUseLd) {
      setLdValue([client.variation(key, defaultValue), LD_STATUSES.FETCHED]);
    }
  }, [client]);
  return ldValue;
};

export const trackLd = async (key, client) => {
  // eslint-disable-next-line no-undef
  if (SERVER) return;
  if (hasLocalStorage && localStorage.getItem('ld_user') !== null) {
    if (client && Object.keys(client).length > 0) {
      client.track(key);
    }
  }
};

export const useReturnTrackLdFn = key => {
  const ldClient = useContext(LDContext);
  const [goalData, setGoalData] = useState({ trackGoalFn: () => {} });
  useEffect(() => {
    setGoalData({
      trackGoalFn: () => {
        trackLd(key, ldClient);
      },
    });
  }, [ldClient]);
  return goalData.trackGoalFn;
};

const getDisplayName = WrappedComponent =>
  WrappedComponent.displayName || WrappedComponent.name || 'Component';

export function withLaunchDarkly<T>(WrappedComponent) {
  const HoC = (props: T) => (
    <LDProvider>
      <WrappedComponent {...props} />
    </LDProvider>
  );
  HoC.displayName = getDisplayName(WrappedComponent);
  return HoC;
}
